import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { icons, miniEducationalProgramsGroups } from 'router/router';
import { useHistory, useLocation } from 'react-router';
import { getUserId } from 'utils/sessionStorage';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useAppSelector } from 'app/hooks';
import { STRIPE_PRICE_NAME } from 'constants/common';

const MiniEducationalProgramsMenu = () => {
  const history = useHistory();
  const location = useLocation();
  const [showSubmenu, setShowSubmenu] = useState<string | null>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const studentId = getUserId();
  const stripeSubscriptionInfo = useAppSelector((store) => store.basicInformation.stripeSubscriptionInfo);

  // Filter out upgradeToSilverPlus if subscription plan is not GradMapStarter
  const filteredMiniEducationalProgramsGroups = useMemo(() => {
    return miniEducationalProgramsGroups.map((route: any) => {
      if (route.isGroup && route.children) {
        const filteredChildren = route.children.filter((child: any) => {
          // Only show upgradeToSilverPlus if subscription plan is GradMapStarter
          if (child?.name === 'Upgrade to Silver+') {
            return stripeSubscriptionInfo?.subcription_plan === STRIPE_PRICE_NAME.GradMapStarter;
          }
          if (child?.name === 'Program Manager') {
            return stripeSubscriptionInfo?.subcription_plan !== STRIPE_PRICE_NAME.GradMapStarter;
          }
          return true;
        });
        return { ...route, children: filteredChildren };
      }
      return route;
    });
  }, [stripeSubscriptionInfo]);

  // Auto-open submenu when component mounts
  useEffect(() => {
    if (filteredMiniEducationalProgramsGroups && filteredMiniEducationalProgramsGroups.length > 0) {
      const route = filteredMiniEducationalProgramsGroups[0];
      if (route.isGroup && route.children && route.children.length > 0) {
        // Auto-open the first group's submenu
        setShowSubmenu(route.name);
      }
    }
  }, [filteredMiniEducationalProgramsGroups]);

  // Handle click outside to close submenu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node)
      ) {
        setShowSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleIconClick = (route: any) => {
    if (route.isGroup === false) {
      history.push(route.route.path);
    } else {
      setShowSubmenu(showSubmenu === route.name ? null : route.name);
    }
  };

  const handleChildClick = (child: any) => {
    history.push(child?.path.replace(':id', studentId));
    setShowSubmenu(null);
  };

  const isActiveRoute = (route: any) => {
    const currentPath = location.pathname;
    
    if (route.isGroup === false) {
      const routePath = route.route.path.replace(':id', '');
      return currentPath === routePath || currentPath.startsWith(routePath);
    } else {
      return route.children.some((child: any) => {
        const childPath = child?.path.replace(':id', '');
        return currentPath === childPath || currentPath.startsWith(childPath);
      });
    }
  };

  const isChildActive = (child: any) => {
    const childPath = child?.path.replace(':id', '');
    // Check exact match first, then check if pathname starts with childPath followed by / or end of string
    // This prevents /summer-programs from matching /summer-programs/saved or /summer-programs/manager
    if (childPath === '/summer-programs') {
      return location.pathname === '/summer-programs' || 
        (location.pathname.startsWith('/summer-programs/') && 
         !location.pathname.startsWith('/summer-programs/saved') &&
         !location.pathname.startsWith('/summer-programs/manager') &&
         !location.pathname.startsWith('/summer-programs/requirements'));
    }
    if (childPath === '/summer-programs/saved') {
      return location.pathname.startsWith('/summer-programs/saved');
    }
    if (childPath === '/summer-programs/manager') {
      return location.pathname.startsWith('/summer-programs/manager');
    }
    return location.pathname === childPath || location.pathname.startsWith(childPath + '/');
  };

  const renderSubmenu = (route: any) => {
    if (!showSubmenu || showSubmenu !== route.name) return null;

    return (
      <div
        ref={submenuRef}
        className="position-absolute bg-white border rounded shadow-sm"
        style={{
          left: '60px',
          top: '0',
          minWidth: '250px',
          zIndex: 1001,
          maxHeight: '400px',
          overflowY: 'auto'
        }}
      >
        <div className="p-2">
          <div className="fw-bold text-primary mb-2">{route.name}</div>
          {route.children.map((child: any, i: number) => {
            const isActive = isChildActive(child);
            const isUpgrade = child?.name === 'Upgrade to Silver+';
            const ChildIcon = child?.icon ? icons[child.icon] : null;
            
            return (
              <div
                key={i}
                className={`d-flex align-items-center p-2 cursor-pointer rounded mini-submenu-item ${
                  isActive && !isUpgrade ? 'bg-primary text-white' : ''
                }`}
                onClick={() => handleChildClick(child)}
                style={{ 
                  cursor: 'pointer',
                  backgroundColor: isUpgrade && isActive ? 'rgba(156, 39, 176, 0.1)' : undefined
                }}
              >
                {ChildIcon && (
                  <ChildIcon 
                    fontSize="small" 
                    className="me-2"
                    sx={{ 
                      color: isUpgrade ? '#9c27b0' : (isActive ? '#ffffff' : '#6c757d')
                    }}
                  />
                )}
                <span 
                  className={`flex-grow-1 ${isUpgrade ? 'text-purple fw-medium' : ''}`}
                  style={{
                    color: isUpgrade ? '#9c27b0' : undefined
                  }}
                >
                  {child?.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div 
        ref={containerRef}
        className="h-100 d-flex flex-column bg-light border-end position-relative" 
        style={{ width: '60px' }}
      >
      <div className="flex-grow-1 d-flex flex-column py-2">
        {filteredMiniEducationalProgramsGroups.map((route: any, index: number) => {
          const IconComponent = icons[route.icon] || 'div';
          const isActive = isActiveRoute(route);
          return (
            <div key={index} className="position-relative">
                <Button
                  variant="outline"
                  className={`d-flex flex-column align-items-center justify-content-center mb-1 mx-1 border-0 position-relative mini-menu-item ${
                    isActive ? 'bg-primary text-white' : 'text-secondary'
                  }`}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '8px'
                  }}
                  onClick={() => handleIconClick(route)}
                >
                  <IconComponent fontSize="small" />
                  </Button>
              
              {renderSubmenu(route)}
            </div>
          );
        })}
      </div>
    </div>
    <style>{`
      .mini-submenu-item:hover {
        background-color: #f8f9fa !important;
      }
    `}</style>
    </>
  );
};

export default MiniEducationalProgramsMenu;
